import { Fragment } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ExternalLink, LayoutGrid } from 'lucide-react';
import type { NavItem, NavDrillGroup } from './nav-types';
import { getActiveDrillItemPath, navRowClasses } from './nav-utils';

export function NavDrillItem({ group, isActive }: { group: NavDrillGroup; isActive: boolean }) {
  const Icon = group.icon;
  return (
    <NavLink
      to={group.basePath}
      data-testid={`nav-drill-${group.basePath}`}
      data-tutorial-id={group.tutorialId}
      data-active={isActive ? 'true' : undefined}
      className={navRowClasses(isActive)}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="flex-1">{group.label}</span>
      <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
    </NavLink>
  );
}

interface NavDrillContentProps {
  group: NavDrillGroup;
  onBack: () => void;
  /**
   * `basePath`s of drill groups that are children of this panel. Items whose
   * `to` matches one of these get a `ChevronRight` glyph so users can tell
   * they open another panel (e.g. Organization → Integrations) instead of a
   * leaf page.
   */
  subDrillBasePaths?: ReadonlySet<string>;
}

export function NavDrillContent({ group, onBack, subDrillBasePaths }: NavDrillContentProps) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const GroupIcon = group.icon;

  const overviewActive = pathname === group.basePath;
  const activeItemPath = getActiveDrillItemPath(group.items, pathname);
  const handleBack = () => {
    if (group.parentBasePath) {
      void navigate(group.parentBasePath);
      return;
    }
    onBack();
  };

  return (
    <div className="flex flex-1 min-h-0 flex-col" data-testid={`nav-drilldown-${group.basePath}`}>
      <button
        type="button"
        onClick={handleBack}
        className="flex items-center gap-2 mx-1.5 mt-1 mb-0.5 rounded-md px-2.5 py-1.5 text-sm text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors"
        aria-label="Back to main navigation"
      >
        <ChevronLeft className="h-3.5 w-3.5 shrink-0" />
        <GroupIcon className="h-4 w-4 shrink-0" />
        <span className="font-medium">{group.label}</span>
      </button>
      <div className="flex flex-1 min-h-0 flex-col gap-0.5 px-1.5 overflow-y-auto">
        <NavLink
          to={group.basePath}
          end
          data-testid={`nav-drill-overview-${group.basePath}`}
          data-active={overviewActive ? 'true' : undefined}
          className={({ isActive }) => navRowClasses(isActive)}
        >
          <LayoutGrid className="h-4 w-4 shrink-0" />
          Overview
        </NavLink>
        {group.items.map(({ to, label, icon: Icon, tutorialId }) => {
          const active = to === activeItemPath;
          const isSubDrill = subDrillBasePaths?.has(to) ?? false;
          return (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-item-${to}`}
              data-tutorial-id={tutorialId}
              data-active={active ? 'true' : undefined}
              className={navRowClasses(active)}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="flex-1">{label}</span>
              {isSubDrill && (
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              )}
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}

interface NavBottomSectionProps {
  /** Plugin-contributed bottom items, permission-filtered. */
  pluginBottomItems: NavItem[];
  /** Top-level drill groups positioned in the bottom column. */
  bottomDrillGroups: NavDrillGroup[];
  /**
   * The URL-resolved drill group, used to highlight a matching bottom drill
   * entry. Pass `null` when no drill group is active.
   */
  activeDrillGroup: NavDrillGroup | null;
  /**
   * Override how bottom drill groups render. The drill style renders
   * `NavDrillItem` rows (default); alternative nav styles inject their own
   * group presentation (expand tree, flat section, flyout trigger).
   */
  renderGroup?: (group: NavDrillGroup, isActive: boolean) => React.ReactNode;
}

/**
 * The pinned footer of the expanded sidebar: plugin-contributed bottom items
 * plus the bottom drill groups. Rendered both at the top
 * level and inside a drill panel so users keep access to these links when they
 * drill into a sub-menu.
 */
export function NavBottomSection({
  pluginBottomItems,
  bottomDrillGroups,
  activeDrillGroup,
  renderGroup,
}: NavBottomSectionProps) {
  const { pathname } = useLocation();
  if (pluginBottomItems.length === 0 && bottomDrillGroups.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-col gap-0.5 px-1.5 pb-2 pt-1">
      <div className="mx-2 mb-1 h-px bg-border/30" />
      {pluginBottomItems.map(({ to, label, icon: Icon, openInNewWindow, tutorialId }) => {
        const active = pathname === to || pathname.startsWith(to + '/');
        const baseClasses = navRowClasses(active);

        if (openInNewWindow) {
          return (
            <a
              key={to}
              href={to}
              target="_blank"
              rel="noreferrer"
              data-testid={`nav-item-${to}`}
              className={baseClasses}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="min-w-0 truncate">{label}</span>
              <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            </a>
          );
        }

        return (
          <NavLink
            key={to}
            to={to}
            data-testid={`nav-item-${to}`}
            data-tutorial-id={tutorialId}
            data-active={active ? 'true' : undefined}
            className={baseClasses}
          >
            <Icon className="h-4 w-4 shrink-0" />
            {label}
          </NavLink>
        );
      })}

      {bottomDrillGroups.map((group) =>
        renderGroup ? (
          <Fragment key={group.basePath}>{renderGroup(group, activeDrillGroup === group)}</Fragment>
        ) : (
          <NavDrillItem key={group.basePath} group={group} isActive={activeDrillGroup === group} />
        ),
      )}
    </div>
  );
}
